import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, User, Phone, Mail, Clock, Send, Star, Image as ImageIcon, Eye, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ChatPhotoUpload } from "./ChatPhotoUpload";
import { useIsMobile } from "@/hooks/use-mobile";
import { TradesmanRatingsDialog } from "./TradesmanRatingsDialog";
import { TradesmanPortfolioDialog } from "./TradesmanPortfolioDialog";

interface JobMessagesInboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  onOpenChat?: (jobId: string, tradesmanId: string) => void;
}

interface TradesmanChat {
  tradesmanId: string;
  tradesmanName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  averageRating?: number;
  totalReviews?: number;
  messages: any[];
  lastMessageAt: string;
  unreadCount: number;
}

export const JobMessagesInbox = ({ open, onOpenChange, jobId, jobTitle, onOpenChat }: JobMessagesInboxProps) => {
  const [chats, setChats] = useState<TradesmanChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<TradesmanChat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const { toast } = useToast();
  const [myProfileId, setMyProfileId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  // Dialog states for ratings and portfolio
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [selectedTradesmanId, setSelectedTradesmanId] = useState<string | null>(null);

  useEffect(() => {
    if (open && jobId) {
      // Reset selection when opening a different job
      setSelectedChat(null);
      fetchJobMessages();
    }
  }, [open, jobId]);

  // Mark messages as read when a chat is selected
  useEffect(() => {
    if (selectedChat && myProfileId) {
      markMessagesAsRead(selectedChat.tradesmanId);
    }
  }, [selectedChat, myProfileId]);

  // When opening the inbox, mark all unread messages for this job as read
  useEffect(() => {
    if (open && jobId && myProfileId) {
      markAllMessagesInJobAsRead();
    }
  }, [open, jobId, myProfileId]);

  const markAllMessagesInJobAsRead = async () => {
    if (!myProfileId || !jobId) return;
    try {
      const { error } = await supabase
        .from("messages")
        .update({ is_read_by_recipient: true })
        .eq("job_id", jobId)
        .eq("recipient_id", myProfileId)
        .eq("is_read_by_recipient", false);

      if (error) {
        console.error("Error marking all job messages as read:", error);
      } else {
        // Refresh the inbox to reflect read status
        fetchJobMessages();
      }
    } catch (error) {
      console.error("Error in markAllMessagesInJobAsRead:", error);
    }
  };

  const markMessagesAsRead = async (tradesmanId: string) => {
    if (!myProfileId || !jobId) return;

    try {
      // Mark all messages from this tradesman in this job as read
      const { error } = await supabase
        .from("messages")
        .update({ is_read_by_recipient: true })
        .eq("job_id", jobId)
        .eq("recipient_id", myProfileId)
        .eq("sender_id", tradesmanId)
        .eq("is_read_by_recipient", false);

      if (error) {
        console.error("Error marking messages as read:", error);
      } else {
        // Refresh the inbox to update unread counts
        fetchJobMessages();
      }
    } catch (error) {
      console.error("Error in markMessagesAsRead:", error);
    }
  };
  const fetchJobMessages = async () => {
    setIsLoading(true);
    try {
      console.log('Inbox: fetching messages for job', jobId);
      // Ensure we have current user's profile id to infer chat participants without profile joins
      let currentProfileId = myProfileId;
      if (!currentProfileId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('You must be logged in to view messages.');
        }
        const { data: myProfile, error: myProfileError } = await supabase
          .from('profiles')
          .select('id, user_type')
          .eq('user_id', user.id)
          .single();
        if (myProfileError) throw myProfileError;
        currentProfileId = myProfile.id;
        setMyProfileId(myProfile.id);
      }
      // Fetch all messages for this job without nested profile joins (blocked by RLS)
      const { data: messagesData, error } = await supabase
        .from('messages')
        .select('id, sender_id, recipient_id, content, created_at, is_read_by_recipient, job_id, message_type, photo_url')
        .eq('job_id', jobId)
        .eq('is_blocked', false)
        .order('created_at', { ascending: false });

      console.log('Inbox: fetched', messagesData?.length || 0, 'messages');
      if (error) throw error;

      // Group messages by the other participant (tradesman) without relying on profile joins
      const chatsByTradesman = new Map<string, TradesmanChat>();
      
      messagesData?.forEach((message: any) => {
        console.log('Processing message:', {
          id: message.id,
          content: message.content,
          senderId: message.sender_id,
          recipientId: message.recipient_id
        });
        
        const otherParticipantId = message.sender_id === currentProfileId ? message.recipient_id : message.sender_id;
        if (!otherParticipantId) {
          console.log('Skipping message - missing other participant id');
          return;
        }

        if (!chatsByTradesman.has(otherParticipantId)) {
          chatsByTradesman.set(otherParticipantId, {
            tradesmanId: otherParticipantId,
            tradesmanName: 'Tradesman',
            companyName: undefined,
            email: undefined,
            phone: undefined,
            messages: [],
            lastMessageAt: message.created_at,
            unreadCount: 0
          });
        }

        const chat = chatsByTradesman.get(otherParticipantId)!;
        chat.messages.push(message);
        
        // Update last message time if this message is newer
        if (new Date(message.created_at) > new Date(chat.lastMessageAt)) {
          chat.lastMessageAt = message.created_at;
        }
        
        // Count unread messages: messages sent by the other participant to me that aren't read
        if (message.sender_id !== currentProfileId && !message.is_read_by_recipient) {
          chat.unreadCount++;
        }
      });

      // Sort messages within each chat by time
      chatsByTradesman.forEach((chat) => {
        chat.messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      });

      // Convert to array and sort by last message time
      const chatsArray = Array.from(chatsByTradesman.values()).sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );

      // Fetch participant details for each chat (could be tradesman or customer)
      for (const chat of chatsArray) {
        try {
          // First try to get as tradesman profile
          const { data: tradesmanData, error: tradesmanError } = await supabase
            .rpc('get_public_tradesman_profile', { tradesman_id: chat.tradesmanId });
          
          if (!tradesmanError && tradesmanData && tradesmanData.length > 0) {
            const profile = tradesmanData[0];
            chat.tradesmanName = profile.company_name || 'Tradesman';
            chat.companyName = profile.company_name;
            chat.averageRating = profile.average_rating;
            chat.totalReviews = profile.total_reviews;
          } else {
            // If not a tradesman, try to get as customer profile
            const { data: customerData, error: customerError } = await supabase
              .from('profiles')
              .select('id, full_name, user_type')
              .eq('id', chat.tradesmanId)
              .single();
            
            if (!customerError && customerData) {
              chat.tradesmanName = customerData.full_name || customerData.user_type === 'customer' ? 'Customer' : 'User';
              chat.companyName = customerData.user_type === 'customer' ? 'Customer' : undefined;
              chat.averageRating = null;
              chat.totalReviews = 0;
            }
          }
        } catch (error) {
          console.error('Error fetching participant profile:', error);
        }
      }

      console.log('Inbox: built chats', chatsArray.map(c => ({ 
        id: c.tradesmanId, 
        name: c.tradesmanName, 
        count: c.messages.length,
        rating: c.averageRating,
        reviews: c.totalReviews
      })));

      setChats(chatsArray);
      
      // Auto-select first chat
      setSelectedChat(chatsArray[0] || null);
      
    } catch (error: any) {
      console.error('Error fetching job messages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenFullChat = (tradesmanId: string) => {
    if (onOpenChat) {
      onOpenChat(jobId, tradesmanId);
      onOpenChange(false);
      toast({
        title: "Opening Chat",
        description: "Starting full conversation...",
      });
    }
  };

  const handlePhotoUploaded = (photoUrl: string, messageId: string) => {
    // Refresh messages to show the new photo
    fetchJobMessages();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'w-[95vw] h-[90vh]' : 'w-[95vw] sm:max-w-6xl h-[85vh]'} overflow-hidden flex flex-col bg-gradient-to-br from-background via-background to-muted/20 border-0 shadow-2xl`}>
        <DialogHeader className="border-b border-border/50 pb-4 mb-0">
          <DialogTitle className="flex items-center gap-3 text-xl font-semibold">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                Message Inbox
              </span>
              <div className="text-sm font-normal text-muted-foreground mt-1">
                {jobTitle}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-center space-y-4">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary mx-auto"></div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/10 to-transparent"></div>
              </div>
              <p className="text-muted-foreground font-medium">Loading conversations...</p>
            </div>
          </div>
        ) : chats.length === 0 ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-center space-y-6 max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center mx-auto">
                <MessageCircle className="h-10 w-10 text-muted-foreground/70" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-foreground">No conversations yet</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Messages from tradesmen will appear here when they contact you about this job.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className={`flex flex-1 min-h-0 ${isMobile ? '' : 'gap-6'} pt-4`}>
            {/* Chat List */}
            <div className={`${isMobile ? 'w-full' : 'w-1/3 min-w-[320px]'} flex flex-col`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg text-foreground">Conversations</h4>
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                  {chats.length}
                </Badge>
              </div>
              
              <ScrollArea className="flex-1 pr-2">
                <div className="space-y-3">
                   {chats.map((chat) => (
                    <div 
                      key={chat.tradesmanId}
                      className={`group relative cursor-pointer transition-all duration-200 ${
                        selectedChat?.tradesmanId === chat.tradesmanId 
                          ? 'transform scale-[1.02]' 
                          : 'hover:transform hover:scale-[1.01]'
                      }`}
                      onClick={() => !isMobile && setSelectedChat(chat)}
                    >
                      <div className={`relative ${isMobile ? 'p-2' : 'p-4'} rounded-2xl border transition-all duration-200 ${
                        selectedChat?.tradesmanId === chat.tradesmanId
                          ? 'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30 shadow-lg shadow-primary/10'
                          : 'bg-card/50 border-border/50 hover:border-border hover:shadow-md'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className="relative">
                            <div className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12'} rounded-xl flex items-center justify-center transition-all duration-200 ${
                              selectedChat?.tradesmanId === chat.tradesmanId
                                ? 'bg-gradient-to-br from-primary to-primary/80 shadow-lg'
                                : 'bg-gradient-to-br from-muted to-muted/50'
                            }`}>
                              <User className={`${isMobile ? 'h-4 w-4' : 'h-6 w-6'} ${
                                selectedChat?.tradesmanId === chat.tradesmanId ? 'text-white' : 'text-muted-foreground'
                              }`} />
                            </div>
                            {chat.unreadCount > 0 && (
                              <div className={`absolute -top-1 -right-1 ${isMobile ? 'w-4 h-4 text-xs' : 'w-5 h-5 text-xs'} bg-destructive rounded-full flex items-center justify-center text-white font-medium shadow-lg`}>
                                {chat.unreadCount}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className={`font-semibold text-foreground truncate ${isMobile ? 'text-xs' : ''}`}>{chat.tradesmanName}</h5>
                            </div>
                            
                            {chat.companyName && (
                              <p className={`text-muted-foreground ${isMobile ? 'mb-1' : 'mb-2'} truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>{chat.companyName}</p>
                            )}
                            
                            {chat.averageRating !== null && chat.averageRating !== undefined ? (
                              <div className={`flex items-center gap-1 ${isMobile ? 'mb-1' : 'mb-3'}`}>
                                <div className={`flex items-center gap-1 px-1.5 py-0.5 bg-yellow-50 dark:bg-yellow-900/20 rounded ${isMobile ? 'text-xs' : ''}`}>
                                  <Star className={`${isMobile ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} fill-yellow-400 text-yellow-400`} />
                                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-yellow-700 dark:text-yellow-300`}>
                                    {chat.averageRating}
                                  </span>
                                </div>
                                <span className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground`}>
                                  ({chat.totalReviews || 0})
                                </span>
                              </div>
                            ) : (
                              <div className={`${isMobile ? 'mb-1' : 'mb-3'}`}>
                                <span className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded`}>
                                  No ratings
                                </span>
                              </div>
                            )}
                            
                            <div className={`flex items-center justify-between ${isMobile ? 'text-xs mb-2' : 'text-xs mb-3'} text-muted-foreground`}>
                              <div className="flex items-center gap-1">
                                <Clock className={`${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                                <span>{format(new Date(chat.lastMessageAt), isMobile ? "MMM d" : "MMM d, h:mm a")}</span>
                              </div>
                              <span className="bg-muted/30 px-1.5 py-0.5 rounded-full text-xs">
                                {chat.messages.length}
                              </span>
                            </div>
                            
                            {isMobile && (
                              <div className="flex gap-1">
                                <Button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTradesmanId(chat.tradesmanId);
                                    setShowRatingsDialog(true);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 h-7 text-xs px-2"
                                >
                                  <Star className="h-2.5 w-2.5 mr-1" />
                                  Rate
                                </Button>
                                <Button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTradesmanId(chat.tradesmanId);
                                    setShowPortfolioDialog(true);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 h-7 text-xs px-2"
                                >
                                  <Eye className="h-2.5 w-2.5 mr-1" />
                                  Work
                                </Button>
                                <Button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenFullChat(chat.tradesmanId);
                                  }}
                                  className="flex-1 h-7 text-xs px-2 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white shadow-lg hover:shadow-primary/25 transition-all duration-200"
                                  size="sm"
                                >
                                  <Send className="h-2.5 w-2.5 mr-1" />
                                  Chat
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Message View - Hidden on mobile */}
            {!isMobile && (
              <div className="flex-1 flex flex-col min-w-0">
                {selectedChat ? (
                  <>
                    {/* Chat Header */}
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <Button 
                          onClick={() => {
                            setSelectedTradesmanId(selectedChat.tradesmanId);
                            setShowRatingsDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="hover:bg-primary/5 hover:border-primary/30"
                        >
                          <Star className="h-4 w-4 mr-2" />
                          Ratings
                        </Button>
                        <Button 
                          onClick={() => {
                            setSelectedTradesmanId(selectedChat.tradesmanId);
                            setShowPortfolioDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="hover:bg-primary/5 hover:border-primary/30"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Portfolio
                        </Button>
                      </div>
                      
                      <Button 
                        onClick={() => handleOpenFullChat(selectedChat.tradesmanId)}
                        className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white shadow-lg hover:shadow-primary/25 transition-all duration-200"
                        size="sm"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Open Full Chat
                      </Button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 bg-gradient-to-b from-muted/20 to-muted/5 border border-border/30 rounded-2xl overflow-hidden">
                      <ScrollArea className="h-full p-4">
                        <div className="space-y-4">
                          {selectedChat.messages.map((message) => {
                            const isFromTradesman = message.sender_id !== myProfileId;
                            return (
                              <div 
                                key={message.id}
                                className={`flex ${isFromTradesman ? 'justify-start' : 'justify-end'} animate-fade-in`}
                              >
                                <div 
                                  className={`max-w-[75%] px-4 py-3 rounded-2xl shadow-sm transition-all duration-200 ${
                                    isFromTradesman 
                                      ? 'bg-white dark:bg-card border border-border/50 text-foreground' 
                                      : 'bg-gradient-to-br from-primary to-primary/90 text-white shadow-primary/20'
                                  } ${isFromTradesman ? 'rounded-bl-md' : 'rounded-br-md'}`}
                                >
                                  {message.message_type === 'photo' && message.photo_url ? (
                                    <div className="space-y-2">
                                      <img 
                                        src={message.photo_url} 
                                        alt="Shared photo"
                                        className="max-w-full h-auto rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(message.photo_url, '_blank')}
                                      />
                                      <p className={`text-xs ${
                                        isFromTradesman ? 'text-muted-foreground' : 'text-white/70'
                                      }`}>
                                        {format(new Date(message.created_at), "h:mm a")}
                                      </p>
                                    </div>
                                  ) : (
                                    <>
                                      <p className="text-sm leading-relaxed">{message.content}</p>
                                      <p className={`text-xs mt-2 ${
                                        isFromTradesman ? 'text-muted-foreground' : 'text-white/70'
                                      }`}>
                                        {format(new Date(message.created_at), "h:mm a")}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center mx-auto">
                        <MessageCircle className="h-8 w-8 text-muted-foreground/70" />
                      </div>
                      <p className="text-muted-foreground font-medium">Select a conversation to view messages</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* Ratings Dialog */}
      <TradesmanRatingsDialog
        tradesmanId={selectedTradesmanId || ""}
        open={showRatingsDialog}
        onOpenChange={setShowRatingsDialog}
      />

      {/* Portfolio Dialog */}
      <TradesmanPortfolioDialog
        tradesmanId={selectedTradesmanId || ""}
        open={showPortfolioDialog}
        onOpenChange={setShowPortfolioDialog}
      />
    </Dialog>
  );
};